import { RootState } from '../../../store'
import {
    selectActiveChartId,
    selectChartConfig,
    selectChartTypes,
    selectDrawings,
    selectTimeBuckets,
} from '../../../selectors/chart'
import { selectPrimarySecurity, selectActiveLayout, selectActiveLayoutId } from '../../../selectors/home'
import { selectUserDashboards } from '../../../selectors/layout'
import { detectPresetCode, findPresetByCode, layoutPresetCatalog, leafPanesInVisualOrder } from './chartLayoutPresets'
import { orderedPaneSlots } from './chartLayoutCommands'
import { buildMultiStrikeTypeCatalog, buildPaneMultiStrikeContext } from './multiStrikeChartCommands'
import { PriceScaleMode } from 'lightweight-charts'
import { chartTypeToJSON } from '../../../proto/generated/chart'
import { exchangeToJSON } from '../../../proto/generated/common'
import { SeriesGroupCategory } from '../../../pages/home'
import { sanitizeId } from '../../../util/indicators'
import { getSecurityFromCache } from '../../../util/security'
import { Security, optionTypeToJSON, securityCategory_CategoryCodeToJSON } from '../../../proto/generated/security'
import { DashboardNamespace } from '../../../proto/generated/user-preference'
import Chart from '../chart'

export type ChartContextChartTypeItem = {
    code: string
    name: string
    group?: string
}

export type ChartContextLayoutPreset = {
    code: string
    name: string
    panes: number
    structure: string
}

export type ChartContextSymbol = {
    exchange: string
    code: string
    symbol?: string
    display_name?: string
    instrument?: string
    category_code?: string
    underlying_security_code?: string
    expiry?: {
        date?: string
        expiry?: number
        name?: string
        weekly_expiry?: boolean
        number_of_days_to_expiry?: number
    }
    strike_price?: number
    option_type?: string
}

export type ChartContextSavedLayout = {
    id: string
    name: string
    is_default: boolean
    pane_count?: number
}

/**
 * Live chart state only. The indicator catalog is deliberately absent: the agent
 * fetches it from `/supercharts/configuration`, and inlining it here pushed the
 * websocket frame past the server's inbound message size limit, which silently
 * dropped the whole chat request.
 */
export type ChartContextSnapshot = {
    active_chart_id: string
    active_layout_id: string
    saved_layouts: ChartContextSavedLayout[]
    charts: Record<string, ChartContextChartSnapshot>
    chart_types: ChartContextChartTypeItem[]
    time_bucket_codes: string[]
    layout: ChartContextLayout
}

export type ChartContextPaneMultiStrike = {
    supported: boolean
    reason?: 'no_fo' | 'no_symbol' | 'expression' | 'multi_security'
    via_underlying?: { exchange: string; code: string; display_name?: string }
    current_type?: string
}

export type ChartContextMultiStrikeType = {
    type: string
    label: string
    available: boolean
    requires?: string
}

/**
 * Dashboard-level window arrangement. `panes` is ordered the same way the agent
 * addresses panes (1-based, visual reading order).
 */
export type ChartContextLayout = {
    /** Preset code when the live tree matches a known arrangement, else null. */
    preset: string | null
    pane_count: number
    orientation: 'columns' | 'rows' | null
    structure: string
    focused_pane: number | null
    panes: Array<{
        pane: number
        widget_type: string
        chart_id?: string
        label?: string
        symbol?: ChartContextSymbol
        time_bucket?: string
        focused: boolean
        multi_strike?: ChartContextPaneMultiStrike
    }>
    sync: Record<string, boolean>
    available_presets: ChartContextLayoutPreset[]
    multi_strike_types: ChartContextMultiStrikeType[]
}

export type ChartContextChartSnapshot = {
    symbol: ChartContextSymbol | null
    time_bucket: {
        code: string
        name?: string
        time_series?: string
        time_value?: number
    } | null
    chart_type?: string | null
    indicators: Array<{
        series_group_id: string
        code: string
        name?: string
        inputs?: Record<string, string>
        outputs?: Record<string, { color?: string; thickness?: number; visibility?: boolean }>
        pane: 'overlay' | 'new'
    }>
    comparisons?: Array<{ exchange: string; code: string; display_name?: string }>
    drawings?: Array<{ id: string; type: string }>
    scale_mode?: string
    /** Latest candle for the primary series, when the live chart is available. */
    last_candle?: {
        time?: number
        open?: number
        high?: number
        low?: number
        close?: number
        volume?: number
    } | null
    /** Current readout per indicator — crosshair values when hovering, else the latest bar. */
    indicator_values?: Record<string, Record<string, number>>
}

type ResolvedChartConfig = ReturnType<ReturnType<typeof selectChartConfig>>

const MAX_CONTEXT_PANE_CHARTS = 4

function primaryPaneId(config: ResolvedChartConfig): string | undefined {
    if (!config) return undefined
    return Object.values(config.panesConfig || {}).find((pane) => pane.primary)?.id
}

const SCALE_MODE_NAMES: Record<number, string> = {
    [PriceScaleMode.Normal]: 'normal',
    [PriceScaleMode.Logarithmic]: 'logarithmic',
    [PriceScaleMode.Percentage]: 'percentage',
    [PriceScaleMode.IndexedTo100]: 'indexed_to_100',
}

function activeScaleMode(config: ResolvedChartConfig): string | undefined {
    const options = config?.scaleOptions
    if (!options) return undefined
    const mode = options.leftScaleVisible && !options.rightScaleVisible ? options.leftScaleMode : options.rightScaleMode
    return SCALE_MODE_NAMES[mode]
}

function round(value: unknown): number | undefined {
    const num = Number(value)
    if (!Number.isFinite(num)) return undefined
    return Math.round(num * 1e4) / 1e4
}

function structuredSymbolFromSecurity(security: Security): ChartContextSymbol {
    const row: ChartContextSymbol = {
        exchange: exchangeToJSON(security.exchange),
        code: security.code,
        symbol: security.symbol,
        display_name: security.displayName || security.symbol,
        instrument: security.instrument,
    }
    if (security.categoryCode != null) {
        row.category_code = securityCategory_CategoryCodeToJSON(security.categoryCode)
    }
    if (security.underlyingSecurityCode) {
        row.underlying_security_code = security.underlyingSecurityCode
    }
    if (security.strikePrice != null) {
        row.strike_price = security.strikePrice
    }
    if (security.optionType != null) {
        row.option_type = optionTypeToJSON(security.optionType)
    }
    if (security.expiry) {
        row.expiry = {
            date: security.expiry.date,
            expiry: security.expiry.expiry,
            name: security.expiry.name,
            weekly_expiry: security.expiry.weeklyExpiry,
            number_of_days_to_expiry: security.expiry.numberOfDaysToExpiry,
        }
    }
    return row
}

function symbolFromWidgetKey(key: string | undefined): ChartContextSymbol | undefined {
    if (!key) return undefined
    const security = getSecurityFromCache(key)
    return security ? structuredSymbolFromSecurity(security) : undefined
}

function paneLabel(displayName: unknown, key: unknown): string | undefined {
    const label = String(displayName || key || '').trim()
    return label || undefined
}

function widgetKeyForChartId(state: RootState, chartId: string): string | undefined {
    const layout = selectActiveLayout(state)
    const layoutId = selectActiveLayoutId(state)
    if (!layout) return undefined
    for (const slot of orderedPaneSlots(layout)) {
        const widget = layout.widgets?.[slot]
        const id = widget?.settings?.chartId ?? (layoutId ? `${layoutId}-${slot}` : undefined)
        if (id === chartId) return widget?.settings?.key
    }
    return undefined
}

function savedLayoutPaneCount(layoutJson: string): number | undefined {
    if (!layoutJson) return undefined
    try {
        const parsed = JSON.parse(layoutJson) as { panes?: Parameters<typeof leafPanesInVisualOrder>[0] }
        if (parsed?.panes) {
            return leafPanesInVisualOrder(parsed.panes).length || undefined
        }
    } catch {
        return undefined
    }
    return undefined
}

function buildSavedLayoutsContext(state: RootState): ChartContextSavedLayout[] {
    return selectUserDashboards(state)
        .filter((dashboard) => !dashboard.isPredefined && dashboard.namespace === DashboardNamespace.CHART)
        .map((dashboard) => {
            const paneCount = savedLayoutPaneCount(dashboard.layout)
            return {
                id: dashboard.id,
                name: dashboard.name,
                is_default: dashboard.isDefault,
                ...(paneCount != null ? { pane_count: paneCount } : {}),
            }
        })
}

function lastCandleRow(chart: Chart): ChartContextChartSnapshot['last_candle'] {
    const candles = chart.getRawData() || []
    const last = candles[candles.length - 1]
    if (!last) return null
    return {
        time: round(last.time),
        open: round(last.open),
        high: round(last.high),
        low: round(last.low),
        close: round(last.close),
        volume: round(last.volume),
    }
}

/**
 * Mirrors the chart legend: the value each indicator is currently showing,
 * keyed by series group so the agent can reference it by the same id it
 * would use in a command.
 */
function indicatorValueRows(
    chart: Chart,
    config: NonNullable<ResolvedChartConfig>,
): Record<string, Record<string, number>> {
    const snapshot = chart.getChartSnapshot()
    const rows: Record<string, Record<string, number>> = {}
    Object.entries(snapshot || {}).forEach(([groupId, seriesMap]) => {
        if (groupId === 'primary') return
        const values: Record<string, number> = {}
        Object.entries(seriesMap || {}).forEach(([seriesId, points]) => {
            const latest = Array.isArray(points) ? points[points.length - 1] : undefined
            const raw = (latest as { value?: number; close?: number })?.value ?? (latest as { close?: number })?.close
            const value = round(raw)
            if (value === undefined) return
            values[config.series?.[seriesId]?.title || seriesId] = value
        })
        if (Object.keys(values).length) rows[groupId] = values
    })
    return rows
}

function compactIndicatorOutputs(
    group: NonNullable<ResolvedChartConfig>['seriesGroups'][string],
): Record<string, { color?: string; thickness?: number; visibility?: boolean }> | undefined {
    const rows: Record<string, { color?: string; thickness?: number; visibility?: boolean }> = {}
    for (const seriesId of group.series || []) {
        const output = group.outputValues?.[seriesId]
        if (!output?.title) continue
        const row: { color?: string; thickness?: number; visibility?: boolean } = {}
        const color = output.colors?.[0]?.colorCode
        if (color) row.color = color
        if (output.thickness != null) row.thickness = output.thickness
        if (output.visibility != null) row.visibility = output.visibility
        rows[output.title] = row
    }
    return Object.keys(rows).length ? rows : undefined
}

function indicatorRowsFromConfig(
    chartConfig: NonNullable<ResolvedChartConfig>,
    includeDetails: boolean,
): ChartContextChartSnapshot['indicators'] {
    const primaryPane = primaryPaneId(chartConfig)
    return Object.values(chartConfig.seriesGroups || {})
        .filter((group) =>
            [SeriesGroupCategory.SERIES_INDICATOR, SeriesGroupCategory.SERIES_VOLUME].includes(group.category),
        )
        .map((group) => {
            const paneConfig = Object.values(chartConfig.panesConfig || {}).find((pane) =>
                (pane?.seriesGroups || []).includes(group.id),
            )
            const pane: 'overlay' | 'new' =
                paneConfig?.id && primaryPane && paneConfig.id === primaryPane ? 'overlay' : 'new'
            const outputSnapshot = includeDetails ? compactIndicatorOutputs(group) : undefined
            return {
                series_group_id: group.id,
                code: sanitizeId(group.scriptCode),
                name: group.name,
                ...(includeDetails ? { inputs: { ...(group.inputValues || {}) } } : {}),
                ...(outputSnapshot ? { outputs: outputSnapshot } : {}),
                pane,
            }
        })
}

function buildCompactChartSnapshot(
    state: RootState,
    chartId: string,
): ChartContextChartSnapshot | undefined {
    const chartConfig = selectChartConfig(chartId)(state)
    if (!chartConfig) return undefined
    const widgetKey = widgetKeyForChartId(state, chartId)
    const symbol = symbolFromWidgetKey(widgetKey) ?? null
    return {
        symbol,
        time_bucket: chartConfig.timeBucket
            ? {
                  code: chartConfig.timeBucket.code,
                  name: chartConfig.timeBucket.name,
                  time_series: String(chartConfig.timeBucket.timeSeries ?? ''),
                  time_value: chartConfig.timeBucket.timeValue,
              }
            : null,
        indicators: indicatorRowsFromConfig(chartConfig, false),
    }
}

/** Chart id for AI context — prefers the layout focused pane over global activeChartId. */
export function resolveContextChartId(state: RootState, chartId?: string | null): string {
    const layout = selectActiveLayout(state)
    const layoutId = selectActiveLayoutId(state)
    if (layout?.activeWidget != null && layoutId) {
        const slots = orderedPaneSlots(layout)
        const focusedSlot = layout.activeWidget
        if (slots.includes(focusedSlot)) {
            const widget = layout.widgets?.[focusedSlot]
            const paneChartId = widget?.settings?.chartId ?? `${layoutId}-${focusedSlot}`
            if (paneChartId) return paneChartId
        }
    }
    return chartId || selectActiveChartId(state)
}

function paneChartIds(state: RootState): string[] {
    const layout = selectActiveLayout(state)
    const layoutId = selectActiveLayoutId(state)
    return orderedPaneSlots(layout)
        .slice(0, MAX_CONTEXT_PANE_CHARTS)
        .map((slot) => {
            const widget = layout?.widgets?.[slot]
            return widget?.settings?.chartId ?? (layoutId ? `${layoutId}-${slot}` : undefined)
        })
        .filter((id): id is string => Boolean(id))
}

/**
 * Prefer a chart config that exists; fall back to global activeChartId when the
 * layout-focused pane id has not been registered yet.
 */
function resolveContextChart(
    state: RootState,
    chartId?: string | null,
): { activeChartId: string; chartConfig: ResolvedChartConfig } {
    const resolvedId = resolveContextChartId(state, chartId)
    const resolvedConfig = resolvedId ? selectChartConfig(resolvedId)(state) : undefined
    if (resolvedConfig) return { activeChartId: resolvedId, chartConfig: resolvedConfig }

    const fallbackId = selectActiveChartId(state)
    if (fallbackId && fallbackId !== resolvedId) {
        const fallbackConfig = selectChartConfig(fallbackId)(state)
        if (fallbackConfig) return { activeChartId: fallbackId, chartConfig: fallbackConfig }
    }
    return { activeChartId: resolvedId, chartConfig: resolvedConfig }
}

function contextSymbol(state: RootState, activeChartId: string): ChartContextSymbol | null {
    const fromWidget = symbolFromWidgetKey(widgetKeyForChartId(state, activeChartId))
    if (fromWidget) return fromWidget

    const primary = selectPrimarySecurity(state)
    if (!primary?.primary) return null
    return structuredSymbolFromSecurity(primary.primary)
}

export function buildChartContextSnapshot(
    state: RootState,
    chartId?: string | null,
    chart?: Chart,
): ChartContextSnapshot {
    const { activeChartId, chartConfig } = resolveContextChart(state, chartId)
    const symbol = contextSymbol(state, activeChartId)
    const drawingsMap = selectDrawings(state)
    const timeBuckets = selectTimeBuckets(state)
    const chartTypes = selectChartTypes(state)
    const activeLayoutId = selectActiveLayoutId(state) || ''

    const charts: Record<string, ChartContextChartSnapshot> = {}
    const paneIds = paneChartIds(state)
    const chartIdsToInclude = paneIds.length ? paneIds : activeChartId ? [activeChartId] : []

    for (const paneChartId of chartIdsToInclude) {
        if (paneChartId === activeChartId && chartConfig) {
            charts[paneChartId] = {
                symbol,
                time_bucket: chartConfig.timeBucket
                    ? {
                          code: chartConfig.timeBucket.code,
                          name: chartConfig.timeBucket.name,
                          time_series: String(chartConfig.timeBucket.timeSeries ?? ''),
                          time_value: chartConfig.timeBucket.timeValue,
                      }
                    : null,
                chart_type: chartConfig.chartType != null ? chartTypeToJSON(chartConfig.chartType) : null,
                indicators: indicatorRowsFromConfig(chartConfig, true),
                comparisons: (chartConfig.comparableSecurities || []).map((security) => ({
                    exchange: String(security.exchange),
                    code: security.code,
                    display_name: security.displayName || security.symbol,
                })),
                drawings: (drawingsMap[paneChartId] || []).slice(0, 20).map((drawing) => ({
                    id: drawing.id,
                    type: drawing.type,
                })),
                scale_mode: activeScaleMode(chartConfig),
                last_candle: chart ? lastCandleRow(chart) : null,
                indicator_values: chart ? indicatorValueRows(chart, chartConfig) : undefined,
            }
            continue
        }

        const compact = buildCompactChartSnapshot(state, paneChartId)
        if (compact) {
            charts[paneChartId] = compact
        }
    }

    if (!Object.keys(charts).length && symbol) {
        charts[activeChartId] = {
            symbol,
            time_bucket: null,
            chart_type: null,
            indicators: [],
            comparisons: [],
            drawings: [],
        }
    }

    return {
        active_chart_id: activeChartId,
        active_layout_id: activeLayoutId,
        saved_layouts: buildSavedLayoutsContext(state),
        charts,
        chart_types: chartTypes.map((chartType) => ({
            code: chartType.code,
            name: chartType.name,
            ...(chartType.group ? { group: chartType.group } : {}),
        })),
        time_bucket_codes: timeBuckets.slice(0, 40).map((bucket) => bucket.code),
        layout: buildLayoutContext(state),
    }
}

function buildLayoutContext(state: RootState): ChartContextLayout {
    const layout = selectActiveLayout(state)
    const layoutId = selectActiveLayoutId(state)
    const slots = orderedPaneSlots(layout)
    const presetCode = detectPresetCode(layout?.panes)
    const preset = presetCode ? findPresetByCode(presetCode) : undefined
    const focusedSlot = layout?.activeWidget
    const focusedIndex = focusedSlot != null ? slots.indexOf(focusedSlot) : -1

    return {
        preset: presetCode,
        pane_count: slots.length || 1,
        orientation: preset?.orientation ?? (layout?.panes?.panes?.length ? (layout.panes.horizontal ? 'columns' : 'rows') : null),
        structure: preset?.structure ?? (slots.length > 1 ? `${slots.length} panes` : 'single pane'),
        focused_pane: focusedIndex >= 0 ? focusedIndex + 1 : null,
        panes: slots.map((slot, index) => {
            const widget = layout?.widgets?.[slot]
            const chartId = widget?.settings?.chartId ?? (layoutId ? `${layoutId}-${slot}` : undefined)
            const paneConfig = chartId ? selectChartConfig(chartId)(state) : undefined
            const label = paneLabel(widget?.settings?.displayName, widget?.settings?.key)
            const structured = symbolFromWidgetKey(widget?.settings?.key)
            const multiStrike = buildPaneMultiStrikeContext(
                widget?.settings?.key,
                String(widget?.widgetType ?? 'CHART'),
                chartId ?? `${layoutId}-${slot}`,
                {
                    expressionMode: widget?.settings?.expressionMode,
                    securitiesCount: widget?.settings?.securities?.length,
                },
            )
            return {
                pane: index + 1,
                widget_type: String(widget?.widgetType ?? 'CHART'),
                ...(chartId ? { chart_id: chartId } : {}),
                ...(label ? { label } : {}),
                ...(structured ? { symbol: structured } : {}),
                ...(paneConfig?.timeBucket?.code ? { time_bucket: paneConfig.timeBucket.code } : {}),
                focused: slot === focusedSlot,
                multi_strike: multiStrike,
            }
        }),
        sync: {
            symbol: Boolean(layout?.syncSettings?.security),
            crosshair: Boolean(layout?.syncSettings?.crosshair),
            interval: Boolean(layout?.syncSettings?.timeBucket),
            time: Boolean(layout?.syncSettings?.time),
            drawings: Boolean(layout?.syncSettings?.drawing),
        },
        available_presets: layoutPresetCatalog(),
        multi_strike_types: buildMultiStrikeTypeCatalog(),
    }
}

export function buildChartAiMetadata(
    state: RootState,
    chartId?: string | null,
    chart?: Chart,
): Record<string, unknown> {
    return {
        chart_context: buildChartContextSnapshot(state, chartId, chart),
    }
}
